import axios from 'axios';
import { withDeduplication } from './apiDeduplication.js';

// API base URL
// Exported too, for the few callers that need the URL without this client's
// interceptors (see hooks/useVersionCheck.js)
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Activity tracking throttle time (5 minutes)
const ACTIVITY_THROTTLE_MS = 5 * 60 * 1000;

// API deduplication: Prevents duplicate parallel API calls.
// If the same API call is made multiple times in parallel (e.g., React StrictMode),
// only one HTTP request is sent and all callers share the same promise.

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // For cookies
});

// Wrapper functions with deduplication
const apiWithDeduplication = {
  get: (url, config) =>
    withDeduplication(
      () => api.get(url, config),
      'GET',
      url,
      config?.params
    ),
  post: (url, data, config) =>
    withDeduplication(
      () => api.post(url, data, config),
      'POST',
      url,
      data
    ),
  put: (url, data, config) =>
    withDeduplication(
      () => api.put(url, data, config),
      'PUT',
      url,
      data
    ),
  patch: (url, data, config) =>
    withDeduplication(
      () => api.patch(url, data, config),
      'PATCH',
      url,
      data
    ),
  delete: (url, config) =>
    withDeduplication(
      () => api.delete(url, config),
      'DELETE',
      url,
      config?.params
    ),
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add activity tracking header every 5 minutes
    const lastActivityUpdate = localStorage.getItem('lastActivityUpdate');
    const now = Date.now();

    if (!lastActivityUpdate || now - parseInt(lastActivityUpdate) > ACTIVITY_THROTTLE_MS) {
      config.headers['X-Update-Activity'] = 'true';
      localStorage.setItem('lastActivityUpdate', now.toString());
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh-token`,
            {},
            {
              withCredentials: true,
            }
          );

          const { accessToken } = response.data.data;
          localStorage.setItem('accessToken', accessToken);

          // Retry original request
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Export the deduplicated API for all service calls
export default apiWithDeduplication;

// Export the original api instance for special cases (like internal retries)
export { api };
